// src/components/BillDetailsModal.jsx
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { X } from "lucide-react";

const BillDetailsModal = ({ 
  isOpen, 
  onClose, 
  billDetails, 
  onGeneratePDF,
  isLoading = false,
  customerId // Add customerId as a prop
}) => {
  if (!isOpen || !billDetails) return null;

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('it-IT', { 
      style: 'currency', 
      currency: 'EUR' 
    }).format(amount);
  };

  // Get pricing model display text
  const getPricingModelText = (model, rate) => {
    switch (model) {
      case 'per_day':
        return `${formatCurrency(rate)}/giorno`;
      case 'monthly_flat':
        return `${formatCurrency(rate)}/mese`;
      case 'no_cost':
        return 'Gratuito';
      default:
        return 'N/A';
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] overflow-y-auto">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50" 
        onClick={onClose}
      ></div>
      
      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative w-full max-w-4xl bg-white rounded-lg shadow-xl">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b">
            <h2 className="text-2xl font-bold text-gray-900">
              Dettagli Fattura - {billDetails.customerName}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X size={24} />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 max-h-[70vh] overflow-y-auto">

            {/* Bill Summary */}
            <div className="bg-blue-50 rounded-lg p-4 mb-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label className="text-sm font-medium text-gray-600">Periodo</Label>
                  <p className="text-lg font-semibold">{billDetails.mese}/{billDetails.anno}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-600">Numero Auto</Label>
                  <p className="text-lg font-semibold">{billDetails.numero_auto}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-600">Importo Totale</Label>
                  <p className="text-2xl font-bold text-green-600">{formatCurrency(billDetails.importo_totale)}</p>
                </div>
              </div>
            </div>

            {/* Customer Info */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-3">Informazioni Cliente</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium text-gray-600">Nome</Label>
                  <p className="text-base">{billDetails.cliente_nome} {billDetails.cliente_cognome}</p>
                </div>
                {billDetails.cliente_email && (
                  <div>
                    <Label className="text-sm font-medium text-gray-600">Email</Label>
                    <p className="text-base">{billDetails.cliente_email}</p>
                  </div>
                )}
                {billDetails.cliente_telefono && (
                  <div>
                    <Label className="text-sm font-medium text-gray-600">Telefono</Label>
                    <p className="text-base">{billDetails.cliente_telefono}</p>
                  </div>
                )}
                <div>
                  <Label className="text-sm font-medium text-gray-600">Modalità Tariffaria</Label>
                  <p className="text-base">
                    {getPricingModelText(
                      billDetails.pricing_model, 
                      billDetails.pricing_model === 'per_day' ? billDetails.daily_rate : billDetails.monthly_rate
                    )}
                  </p>
                </div>
              </div>
            </div>

            {/* Car Details */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-3">
                Dettaglio Veicoli ({billDetails.car_details?.length || 0} auto)
              </h3>
              
              {billDetails.car_details && billDetails.car_details.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full border border-gray-200 rounded-lg">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="text-left p-3 font-medium text-gray-700">Targa</th>
                        <th className="text-left p-3 font-medium text-gray-700">Telaio</th>
                        <th className="text-left p-3 font-medium text-gray-700">Marca/Modello</th>
                        <th className="text-left p-3 font-medium text-gray-700">Data Ingresso</th>
                        <th className="text-left p-3 font-medium text-gray-700">Data Uscita</th>
                        {billDetails.pricing_model === 'per_day' && (
                          <>
                            <th className="text-right p-3 font-medium text-gray-700">Giorni</th>
                            <th className="text-right p-3 font-medium text-gray-700">Importo</th>
                          </>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {billDetails.car_details.map((car, index) => (
                        <tr key={index} className="border-t border-gray-200 hover:bg-gray-50">
                          <td className="p-3 font-mono text-sm">{car.targa}</td>
                          <td className="p-3 font-mono text-xs">{car.numero_telaio}</td>
                          <td className="p-3">{car.marca} {car.modello}</td>
                          <td className="p-3">{new Date(car.data_ingresso).toLocaleDateString('it-IT')}</td>
                          <td className="p-3">
                            {car.data_uscita ? new Date(car.data_uscita).toLocaleDateString('it-IT') : 'In corso'}
                          </td>
                          {billDetails.pricing_model === 'per_day' && (
                            <>
                              <td className="p-3 text-right font-semibold">{car.days_parked || 0}</td>
                              <td className="p-3 text-right font-semibold">{formatCurrency(car.amount || 0)}</td>
                            </>
                          )}
                        </tr>
                      ))}
                    </tbody>
                    {billDetails.pricing_model === 'per_day' && (
                      <tfoot className="bg-gray-50 border-t-2 border-gray-300">
                        <tr>
                          <td colSpan="5" className="p-3 text-right font-bold">Totale:</td>
                          <td className="p-3 text-right font-bold">
                            {billDetails.car_details.reduce((sum, car) => sum + (car.days_parked || 0), 0)} giorni
                          </td>
                          <td className="p-3 text-right font-bold text-green-600">
                            {formatCurrency(billDetails.importo_totale)}
                          </td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
              ) : (
                <p className="text-gray-500 text-center py-4">Nessun dettaglio auto disponibile</p>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end space-x-3 p-6 border-t bg-gray-50">
            <Button variant="outline" onClick={onClose}>
              Chiudi
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BillDetailsModal;